import { useEffect, useState } from 'react'
import { executiveApi, type ExecutiveKpiExtended, type ExecutiveFilters, type KpiProjection, type KpiRevenueRow } from '../api/executive'
import { useFeatureFlag } from '../lib/featureFlags'
import { Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, LoadingState, StatCard, StatGrid } from './ui'

/**
 * C-02 - KPI commerciali estesi nel cockpit Direzione (flag `report.kpi_estesi`): conversione, tassi di attivazione con
 * nomi distinti, previsione di fine mese in due versioni (lineare e per giorni lavorativi) e, solo con la lettura
 * amministrativa, ricavo per agente e per pratica nei tre stati affiancati (presunto, maturato, incassato).
 */

const eur = (n: number) => n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
const num = (n: number) => n.toLocaleString('it-IT', { maximumFractionDigits: 1 })
const pct = (r: number | null | undefined) => (r == null ? '—' : `${(r * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%`)

const RATE_LABELS: Record<string, string> = {
  activationRateVsSubmitted: 'Attivazione su inviate',
  activationRateOnContracts: 'Attivazione su inserite',
}

function ProjectionTiles({ label, p, money }: { label: string; p: KpiProjection; money: boolean }) {
  const f = money ? eur : num
  return (
    <>
      <StatCard label={`${label}: lineare`} value={f(p.linear)} supporting={`${p.elapsedDays} giorni su ${p.totalDays}`} />
      <StatCard
        label={`${label}: lavorativi`}
        value={p.workingDays == null ? '—' : f(p.workingDays)}
        supporting={`${p.elapsedWorkingDays} giorni lavorativi su ${p.totalWorkingDays}`}
      />
    </>
  )
}

function RevenueTable({ title, rows }: { title: string; rows: KpiRevenueRow[] }) {
  return (
    <Card padding="none">
      <CardHeader title={title} subtitle="Presunto, maturato e incassato nel periodo" />
      <CardContent>
        <DataTable variant="plain">
          <thead>
            <tr><th>{title.includes('agente') ? 'Agente' : 'Pratica'}</th><th className="text-end">Presunto</th><th className="text-end">Maturato</th><th className="text-end">Incassato</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td className="text-end md-num">{eur(r.states.presumed)}</td>
                <td className="text-end md-num">{eur(r.states.accrued)}</td>
                <td className="text-end md-num">{eur(r.states.collected)}</td>
              </tr>
            ))}
            {rows.length === 0 && <DataTableEmptyRow colSpan={4} title="Nessun ricavo nel periodo." />}
          </tbody>
        </DataTable>
      </CardContent>
    </Card>
  )
}

export function KpiEstesiPanel({ filters }: { filters: ExecutiveFilters }) {
  const flagOn = useFeatureFlag('report.kpi_estesi')
  const [data, setData] = useState<ExecutiveKpiExtended | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!flagOn) return
    let alive = true
    setData(null)
    setError('')
    executiveApi.kpiExtended(filters)
      .then(d => { if (alive) setData(d) })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : 'Errore') })
    return () => { alive = false }
  }, [flagOn, filters])

  if (!flagOn) return null
  if (error) return <Callout tone="warning">KPI commerciali non disponibili. {error}</Callout>
  if (!data) return <LoadingState />
  if (!data.enabled) return null

  const rev = data.revenue
  return (
    <>
      <StatGrid>
        <StatCard
          label="Conversione"
          value={pct(data.conversion?.rate)}
          supporting={`${data.conversion?.numerator ?? 0} attivate su ${data.conversion?.denominator ?? 0} in pipeline`}
        />
        {data.activationRates?.map(r => (
          <StatCard key={r.name} label={RATE_LABELS[r.name] ?? r.name} value={pct(r.rate)} supporting={`${r.numerator} su ${r.denominator}`} />
        ))}
        {rev && <StatCard label="Tasso d'incasso" value={pct(rev.collectionRate)} supporting="Incassato su maturato del periodo" />}
        {data.projection && <ProjectionTiles label="Attivate a fine mese" p={data.projection.activated} money={false} />}
        {rev && <ProjectionTiles label="Maturato a fine mese" p={rev.projection.accrued} money />}
        {rev && <ProjectionTiles label="Incassato a fine mese" p={rev.projection.collected} money />}
      </StatGrid>
      {rev && (
        <>
          <StatGrid>
            <StatCard label="Ricavo presunto" value={eur(rev.total.presumed)} supporting="Atteso x probabilita', non maturato" />
            <StatCard label="Ricavo maturato" value={eur(rev.total.accrued)} supporting="Maturato nel periodo" />
            <StatCard label="Ricavo incassato" value={eur(rev.total.collected)} supporting="Incassato nel periodo" />
          </StatGrid>
          <RevenueTable title="Ricavo per agente" rows={rev.byAgent} />
          <RevenueTable title="Ricavo per pratica" rows={rev.byContract} />
        </>
      )}
      {data.limitations && (
        <ul className="md-typescale-body-small text-muted mb-0">
          {data.limitations.map(l => <li key={l}>{l}</li>)}
        </ul>
      )}
    </>
  )
}
